import { useEffect, useRef } from 'react';

import type { ObjectiveFormData } from '../validation';
import type { User } from '@/types/directory/User';
import type { ObjectiveParticipantAttribute } from '@/types/objectives';
import type { UseFormGetValues, UseFormSetValue } from 'react-hook-form';

import { useGetUsersByIds } from '@/api/directory';
import { useResolveParticipantAttributes } from '@/api/objectives';
import { getFullName, getInitials, getProfilePictureUrl } from '@/utils/user';

/** Someone a key result may be handed to, as the responsible select shows them. */
export interface EmployeeOption {
    id: string;
    label: string;
    initials: string;
    avatarUrl: string;
}

interface UseKeyResultResponsiblesParams {
    objectiveId?: string;
    employeeIds: string[];
    participantAttributes: ObjectiveParticipantAttribute[];
    /** Editing only ever keeps the attributes of someone who may not add one. */
    canResolveAttributes: boolean;
    savedResponsibles: ReadonlyMap<string, string>;
    getValues: UseFormGetValues<ObjectiveFormData>;
    setValue: UseFormSetValue<ObjectiveFormData>;
}

/**
 * The people a key result may be handed to -- the owner, the people listed by name and the members
 * of the chosen attributes -- as select options. A responsible chosen here who leaves that set is
 * cleared, since the API refuses them; one still on the responsible it was saved with is kept.
 */
export const useKeyResultResponsibles = ({
    objectiveId,
    employeeIds,
    participantAttributes,
    canResolveAttributes,
    savedResponsibles,
    getValues,
    setValue,
}: UseKeyResultResponsiblesParams): EmployeeOption[] => {
    const {
        data: attributeResolution,
        isSuccess: isAttributeResolutionAnswered,
        isPlaceholderData: isAttributeResolutionOutdated,
    } = useResolveParticipantAttributes({
        attributes: participantAttributes,
        objectiveId,
        enabled: canResolveAttributes,
    });

    // Undefined while the attribute members are on their way or could not be read.
    const readKnownCandidateIds = (): string[] | undefined => {
        if (participantAttributes.length === 0) {
            return employeeIds;
        }

        if (
            !attributeResolution ||
            !isAttributeResolutionAnswered ||
            isAttributeResolutionOutdated
        ) {
            return undefined;
        }

        return [...new Set([...employeeIds, ...attributeResolution.employeeIds])];
    };
    const knownCandidateIds = readKnownCandidateIds();

    // Meanwhile the previous answer keeps the responsibles already chosen on screen, and a saved
    // responsible stays offered even once they are no longer a member: the save sends them back.
    const optionIds = [
        ...new Set([
            ...(knownCandidateIds ?? [...employeeIds, ...(attributeResolution?.employeeIds ?? [])]),
            ...savedResponsibles.values(),
        ]),
    ];

    const { data: users = [] } = useGetUsersByIds({
        userIds: optionIds,
        identifier: 'objective-form-employees',
    });
    // Everyone already named, kept across renders: the query is keyed by the ids being changed, so
    // each addition would otherwise blank every label until its round trip lands.
    const knownUsersRef = useRef(new Map<string, User>());
    users.forEach((user) => knownUsersRef.current.set(user.id, user));

    // The set spelled as a string only changes when the set does.
    const knownCandidatesKey = knownCandidateIds?.join(',');

    useEffect(() => {
        if (knownCandidatesKey === undefined) {
            return;
        }

        const candidateIds = new Set(knownCandidatesKey.split(','));
        getValues('keyResults').forEach((keyResult, index) => {
            const responsibleId = keyResult.responsibleEmployeeId;
            const isSavedResponsible =
                !!keyResult.id && savedResponsibles.get(keyResult.id) === responsibleId;
            if (responsibleId && !candidateIds.has(responsibleId) && !isSavedResponsible) {
                setValue(`keyResults.${index}.responsibleEmployeeId`, undefined);
            }
        });
    }, [knownCandidatesKey, savedResponsibles, getValues, setValue]);

    return optionIds
        .map((employeeId) => knownUsersRef.current.get(employeeId))
        .filter((user): user is User => !!user)
        .map((user) => ({
            id: user.id,
            label: getFullName(user),
            initials: getInitials(user),
            avatarUrl: getProfilePictureUrl(user.profilePictureUrl),
        }));
};
